import React from 'react';
import PropTypes from 'prop-types';

import { Stack, Typography, Button, FormControl, MenuItem, Box, } from '@mui/material';
import API from '../../../data/api';
import CustomTextField from '../../custom/CustomTextField';
import CustomModal from '../../custom/CustomModal';
import CustomDatePicker from '../../custom/CustomDatePicker';
import { extractFormValues, isNotNullValue, isNullValue, validateForm } from '../../../utils/formUtil';
import { MODAL_WIDTH } from '../../../utils/constants';
import { LoadingButton } from '@mui/lab';

const createBaseFormData = () => ({
  selectedDepot: { value: '', required: true, error: false, errorMessage: 'Please select a depot' },
  startDate: { value: null, required: true, error: false, errorMessage: 'Please specify a start date' },
  endDate: { value: null, required: true, error: false, errorMessage: 'Please specify an end date' },
});

const createInitialState = () => ({
  depots: [],
  products: [],
  clearDates: true,
  submitted: false,
  formData: createBaseFormData(),
  alert: '',
});

export default class DepotPricingModal extends React.Component {

  constructor(props) {
    super(props);
    this.state = createInitialState();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps?.openModal && this.props.openModal) {
      this.fetchPricingMetadata();
    }
  }

  getProductFieldName = (productId) => `product_${productId}`;

  buildDynamicProductFields = (products = []) => {
    return products.reduce((accumulator, product) => {
      const fieldName = this.getProductFieldName(product.id);
      accumulator[fieldName] = {
        value: '',
        required: true,
        error: false,
        errorMessage: `Please provide ${product.name} price`,
      };
      return accumulator;
    }, {});
  };

  fetchPricingMetadata = async () => {
    try {
      const [depotsResponse, productsResponse] = await Promise.all([
        API.get('inventory/backoffice/depots?depot_type=pricing'),
        API.get('inventory/backoffice/products'),
      ]);

      const depots = depotsResponse?.status === 200 ? depotsResponse?.data?.depots || [] : [];
      const products = productsResponse?.status === 200 ? productsResponse?.data?.products || [] : [];

      const dynamicProductFields = this.buildDynamicProductFields(products);
      const nextFormData = {
        ...createBaseFormData(),
        ...dynamicProductFields,
      };

      this.setState({
        depots,
        products,
        formData: nextFormData,
        alert: products.length === 0 ? 'No products found for depot pricing.' : '',
      });
    } catch (error) {
      console.error('There was an error retrieving depot pricing metadata:', error);
      this.setState({
        depots: [],
        products: [],
        formData: createBaseFormData(),
        alert: 'Unable to load depots/products for pricing.',
      });
    }
  }

  submitPricing = async (event) => {
    event.preventDefault();

    if (!validateForm(this.setValidatedFormData, this.state.formData)) {
      this.setState({  submitted: false, });
      return;
    }

    if (!this.state.products?.length) {
      this.setState({
        submitted: false,
        alert: 'No products available to submit pricing.',
      });
      return;
    }

    this.setState({ submitted: true, });
    const { selectedDepot, startDate, endDate, ...productFieldValues } = extractFormValues(this.state.formData);
    const endpoint = `inventorybackoffice/enter_selling_price?depot_id=${selectedDepot}&start_date=${startDate}&end_date=${endDate}`;
    const productPrices = this.state.products
      .map((product) => {
        const fieldName = this.getProductFieldName(product.id);
        const rawPrice = productFieldValues[fieldName];
        const numericPrice = Number(rawPrice);

        if (!Number.isFinite(numericPrice)) {
          return null;
        }

        return { product: product.name, price: numericPrice };
      })
      .filter(Boolean);

    if (productPrices.length === 0) {
      this.setState({
        submitted: false,
        alert: 'No valid product prices were provided.',
      });
      return;
    }

    await API.post(endpoint, productPrices)
      .then(res => {
        if (res.status === 200 || res.status === 201) {
          this.handleCloseModal();
        } else {
          this.setState({
            submitted: false,
            alert: 'There was an error processing your order...'
          })
        }
      })
      .catch((error) => {
        this.setState({
          submitted: false,
          alert: 'There was an error processing your order.',
        });
        console.error('There was an error submitting your order, ', error);
      });
  }

  setValidatedFormData = (formData) => {
    this.setState({
      formData: { ...formData },
    });
  }

  toggleClearDates = () => {
    this.setState({
      clearDates: !this.state.clearDates,
    });
  }

  handleCloseModal = () => {
    this.setState(createInitialState());
    this.props.toggleModalState("price", false);
  }

  updateFiledValue = (field, value, data) => {
    const currentData = data || this.state.formData;
    // console.log(field, value, isNullValue(value), currentData)

    const newFormData = {
      ...currentData,
      [field]: {
        value: value,
        error: isNullValue(value),
      } ,
    };
  
    this.setState({
      formData: {...newFormData}
    });

    return newFormData;
  }
  handleValueChange = (e) => {
    this.updateFiledValue(e.target.name, e.target.value);
  }

  handleDepotSelect = (event) => {

    let newFormData = { ...this.state.formData };
    const { name, value } = event.target;
    newFormData = this.updateFiledValue(name, value, newFormData);
    const currentDepot = this.state.depots?.find((d) => String(d.depot_id) === String(value));
    if (isNotNullValue(currentDepot)) {
      let selectedStartDate = null;
      let selectedEndDate = null;

      this.state.products.forEach((product) => {
        const fieldName = this.getProductFieldName(product.id);
        const priceRecord = (currentDepot?.prices || []).find((item) =>
          String(item?.product_id || '') === String(product.id)
          || String(item?.product_name || '').toLowerCase() === String(product.name || '').toLowerCase());

        const price = priceRecord?.price_details?.sell_price ?? '';
        const startDate = priceRecord?.price_details?.start_trading_date?.split('T')[0] || null;
        const endDate = priceRecord?.price_details?.end_trading_date?.split('T')[0] || null;

        if (!selectedStartDate && startDate) {
          selectedStartDate = startDate;
        }
        if (!selectedEndDate && endDate) {
          selectedEndDate = endDate;
        }

        newFormData = {
          ...newFormData,
          [fieldName]: {
            ...newFormData[fieldName],
            value: price,
            error: isNullValue(price),
          },
        };
      });

      newFormData = {
        ...newFormData,
        startDate: {
          ...newFormData.startDate,
          value: selectedStartDate,
          error: isNullValue(selectedStartDate),
        },
        endDate: {
          ...newFormData.endDate,
          value: selectedEndDate,
          error: isNullValue(selectedEndDate),
        },
      };

      this.setState({
        formData: newFormData,
      });
      return;
    }

    this.state.products.forEach((product) => {
      const fieldName = this.getProductFieldName(product.id);
      newFormData = {
        ...newFormData,
        [fieldName]: {
          ...newFormData[fieldName],
          value: '',
          error: true,
        },
      };
    });

    newFormData = {
      ...newFormData,
      startDate: {
        ...newFormData.startDate,
        value: null,
        error: true,
      },
      endDate: {
        ...newFormData.endDate,
        value: null,
        error: true,
      },
    };

    this.setState({
      formData: newFormData,
    });

  }

  render() {
    const { depots, products } = this.state;
    return (
      <Stack>
        <CustomModal
          openModal={this.props.openModal}
          handleCloseModal={this.handleCloseModal}
          modalStyle={{
            width: { ...MODAL_WIDTH.small },
          }}
          modalTitle={'Depot Pricing'}
          modalIcon='currency'
        >
          <form onSubmit={this.submitPricing} noValidate>
            <CustomTextField sx={{ my: 3 }}
              fullWidth
              select
              name='selectedDepot'
              label="Select Depot"
              labelId="depot-select-label"
              value={this.state.formData.selectedDepot.value}
              onChange={(e) => { this.handleDepotSelect(e) }}
              required={this.state.formData.selectedDepot.required}
              error={this.state.formData.selectedDepot.error}
              helperText={this.state.formData.selectedDepot.error && this.state.formData.selectedDepot.errorMessage}
            >
              <MenuItem value=" " disabled>
                <em>Select Depot</em>
              </MenuItem>
              {depots.map((item) => (
                <MenuItem key={'sepot_' + item.depot_id} value={item.depot_id}>
                  {item.depot_name}
                </MenuItem>
              ))}
            </CustomTextField>

            {products.map((product) => {
              const fieldName = this.getProductFieldName(product.id);
              const fieldState = this.state.formData[fieldName] || {};
              return (
                <CustomTextField sx={{ my: 1 }}
                  key={`product-price-${product.id}`}
                  fullWidth
                  type="number"
                  id={`product-${product.id}-price`}
                  variant="outlined"
                  name={fieldName}
                  label={`${product.name} Price`}
                  onChange={(e) => { this.handleValueChange(e) }}
                  value={fieldState.value || ''}
                  required={fieldState.required}
                  error={fieldState.error}
                  helperText={fieldState.error && fieldState.errorMessage}
                />
              );
            })}

            {products.length === 0 && (
              <Typography variant="body2" color="error.main" sx={{ my: 1 }}>
                No products found for depot pricing.
              </Typography>
            )}

            <FormControl fullWidth >
              <CustomDatePicker sx={{ overflow: 'hidden' }}
                label={"Start Date"}
                field={'startDate'}
                currentValue={this.state.formData.startDate.value}
                handleChange={this.updateFiledValue}
                clear={this.state.clearDates}
                setClear={this.toggleClearDates}
                dateError={this.state.formData.startDate.error && 'invalidDate'}
              />
            </FormControl>

            <FormControl sx={{ mt: 1, }} fullWidth>
              <CustomDatePicker
                required
                label={"End Date"}
                field={'endDate'}
                currentValue={this.state.formData.endDate.value}
                handleChange={this.updateFiledValue}
                clear={this.state.clearDates}
                setClear={this.toggleClearDates}
                minDate={this.state.formData.startDate.value}
                dateError={this.state.formData.endDate.error && 'invalidDate'}
              />
            </FormControl>

            <Box sx={{ alignItems: 'center',  mt: 3, }}>
              <Button color="inherit"
                variant='contained'
                sx={{ width: '47%' }}
                onClick={() => { this.handleCloseModal() }}
              >
                Cancel
              </Button>
              <LoadingButton
                variant='contained'
                sx={{ml: 2, width: '47%' }}
                type="submit"
                loading={this.state.submitted}
                disabled={products.length === 0}
              // onClick={(e) => { this.submitPricing(e) }}
              >
                Submit
              </LoadingButton>
            </Box>
            <Typography sx={{ mt: 2 }}>
              {this.state.alert}
            </Typography>
          </form>
        </CustomModal>
      </Stack>
    )
  }
}

DepotPricingModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  toggleModalState: PropTypes.func.isRequired,
};
